import React, { useEffect, useState } from "react";
import { useCurrency } from "../context/CurrencyContext";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  BarChart3, 
  Users, 
  Clock, 
  DollarSign, 
  Search,
  Star,
  Filter,
  ArrowUpDown,
  Loader2,
  FileText,
  TrendingUp
} from "lucide-react";

const ServiceRatesPage = () => {
  const { formatAmount } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterService, setFilterService] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [viewMode, setViewMode] = useState("artist"); // artist or service

  useEffect(() => {
    fetchData();
    fetchServiceTypes();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.get("/art-director/service-rates-analytics");
      setData(response.data);
    } catch (error) {
      console.error("Error fetching service rates:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceTypes = async () => {
    try {
      const res = await api.get("/service-types/list");
      if (res.data && res.data.length > 0) {
        setServiceTypes(res.data.map(s => s.name));
      }
    } catch (error) {
      console.error("Error fetching service types:", error);
    }
  };

  // Get all service types - prefer from service_types collection, fallback to data keys
  const allServiceTypes = serviceTypes.length > 0 
    ? serviceTypes 
    : [
        ...new Set([
          ...Object.keys(data?.declared?.by_service || {}),
          ...Object.keys(data?.actual?.by_service || {})
        ])
      ].sort();

  // Filter and sort artists for declared rates
  const filteredDeclaredArtists = (data?.declared?.by_artist || []).filter(artist => {
    const matchesSearch = artist.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesService = filterService === "all" || artist.rates?.[filterService];
    return matchesSearch && matchesService;
  });

  // Filter and sort artists for actual rates
  const filteredActualArtists = (data?.actual?.by_artist || []).filter(artist => {
    const matchesSearch = artist.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesService = filterService === "all" || artist.services?.[filterService];
    return matchesSearch && matchesService;
  });

  const sortArtists = (artists, isDeclared = false) => {
    return [...artists].sort((a, b) => {
      let aVal, bVal;
      if (sortBy === "name") {
        aVal = a.name || "";
        bVal = b.name || "";
      } else if (sortBy === "rating") {
        aVal = a.rating || 0;
        bVal = b.rating || 0;
      } else if (sortBy === "rate") {
        if (isDeclared) {
          // For declared rates - average hourly rate from all services
          const aRates = Object.values(a.rates || {}).map(r => r.hourly_rate).filter(r => r > 0);
          const bRates = Object.values(b.rates || {}).map(r => r.hourly_rate).filter(r => r > 0);
          aVal = aRates.length ? aRates.reduce((s, r) => s + r, 0) / aRates.length : 0;
          bVal = bRates.length ? bRates.reduce((s, r) => s + r, 0) / bRates.length : 0;
        } else {
          aVal = a.overall_hourly_rate || 0;
          bVal = b.overall_hourly_rate || 0;
        }
      } else if (sortBy === "amount") {
        aVal = a.total_amount || 0;
        bVal = b.total_amount || 0;
      }
      
      if (typeof aVal === "string") {
        return sortOrder === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-orange-500" />
            Аналітика ставок
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Compare declared vs actual artist rates
          </p>
        </div>
      </div>

      <Tabs defaultValue="declared" className="space-y-4">
        <TabsList className="bg-zinc-800 border-zinc-700">
          <TabsTrigger value="declared" className="data-[state=active]:bg-blue-600">
            <FileText className="h-4 w-4 mr-2" />
            Declared Rates
          </TabsTrigger>
          <TabsTrigger value="actual" className="data-[state=active]:bg-green-600">
            <TrendingUp className="h-4 w-4 mr-2" />
            Actual Rates
          </TabsTrigger>
        </TabsList>

        {/* ============ DECLARED RATES TAB ============ */}
        <TabsContent value="declared" className="space-y-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-blue-400 text-lg">Declared Rates</CardTitle>
              <CardDescription className="text-zinc-500">
                Fixed price and estimated hours per service type declared by artists
              </CardDescription>
            </CardHeader>
          </Card>

          {/* View Mode Toggle */}
          <div className="flex gap-2">
            <Button
              variant={viewMode === "artist" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("artist")}
              className={viewMode === "artist" ? "bg-blue-600" : "border-zinc-700"}
            >
              <Users className="h-4 w-4 mr-2" />
              By Artist
            </Button>
            <Button
              variant={viewMode === "service" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("service")}
              className={viewMode === "service" ? "bg-blue-600" : "border-zinc-700"}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              By Service Type
            </Button>
          </div>

          {viewMode === "artist" ? (
            <>
              {/* Filters */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                      <Search className="h-4 w-4 text-zinc-500" />
                      <Input
                        placeholder="Search artist..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-zinc-50"
                      />
                    </div>
                    <Select value={filterService} onValueChange={setFilterService}>
                      <SelectTrigger className="w-[180px] bg-zinc-800 border-zinc-700">
                        <SelectValue placeholder="Service Type" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="all" className="text-zinc-50">All Services</SelectItem>
                        {allServiceTypes.map(type => (
                          <SelectItem key={type} value={type} className="text-zinc-50 capitalize">
                            {type.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="w-[150px] bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="name" className="text-zinc-50">Ім'я</SelectItem>
                        <SelectItem value="rating" className="text-zinc-50">Rating</SelectItem>
                        <SelectItem value="rate" className="text-zinc-50">Сер. ставка</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                    >
                      {sortOrder === "asc" ? "↑" : "↓"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Declared Rates Table - By Artist */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-0 overflow-x-auto">
                  <Table className="min-w-max">
                    <TableHeader>
                      <TableRow className="border-zinc-800">
                        <TableHead className="text-zinc-400 sticky left-0 bg-zinc-900 z-10">Artist</TableHead>
                        <TableHead className="text-zinc-400 text-center">Rating</TableHead>
                        {allServiceTypes.map(type => (
                          <TableHead key={type} className="text-zinc-400 text-center text-xs whitespace-nowrap px-2" colSpan={1}>
                            {type.length > 15 ? type.substring(0, 15) + "..." : type}
                          </TableHead>
                        ))}
                      </TableRow>
                      <TableRow className="border-zinc-800 bg-zinc-800/30">
                        <TableHead className="sticky left-0 bg-zinc-800/30 z-10"></TableHead>
                        <TableHead></TableHead>
                        {allServiceTypes.map(type => (
                          <TableHead key={`${type}-sub`} className="text-zinc-500 text-center text-[10px] px-2">
                            price / hrs / $/h
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortArtists(filteredDeclaredArtists, true).map(artist => (
                        <TableRow key={artist.id} className="border-zinc-800">
                          <TableCell className="sticky left-0 bg-zinc-900 z-10">
                            <span className="text-zinc-50 font-medium whitespace-nowrap">{artist.name}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            {artist.rating ? (
                              <div className="flex items-center justify-center gap-1">
                                <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                                <span className="text-yellow-500 text-sm">{artist.rating}</span>
                              </div>
                            ) : "—"}
                          </TableCell>
                          {allServiceTypes.map(type => {
                            const rateData = artist.rates?.[type];
                            return (
                              <TableCell key={type} className="text-center px-2">
                                {rateData ? (
                                  <div className="flex flex-col items-center text-xs space-y-0.5">
                                    <span className="text-orange-400 font-mono">
                                      {rateData.price ? `$${rateData.price}` : "—"}
                                    </span>
                                    <span className="text-zinc-500">
                                      {rateData.hours ? `${rateData.hours}h` : "—"}
                                    </span>
                                    <span className="text-blue-400 font-mono text-[10px]">
                                      {rateData.hourly_rate ? `$${rateData.hourly_rate}/h` : "—"}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-zinc-600">—</span>
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {filteredDeclaredArtists.length === 0 && (
                    <div className="text-center py-12 text-zinc-500">
                      <Users className="h-10 w-10 mx-auto mb-3 opacity-50" />
                      <p>Artistи не вказали свої ставки</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            /* Declared Rates - By Service Type */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(data?.declared?.by_service || {}).map(([serviceType, stats]) => (
                <Card key={serviceType} className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-zinc-50 text-lg capitalize flex items-center justify-between">
                      {serviceType.replace(/_/g, ' ')}
                      <Badge className="bg-blue-900/30 text-blue-400 border-blue-700">
                        {stats.count} artists
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Price Stats */}
                    {stats.price_stats && (
                      <div className="p-3 bg-zinc-800/50 rounded-lg">
                        <p className="text-xs text-zinc-500 mb-2">Фіксована ціна ($)</p>
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div>
                            <p className="text-xs text-zinc-500">Min</p>
                            <p className="text-sm font-mono text-red-400">${stats.price_stats.min}</p>
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500">Avg</p>
                            <p className="text-sm font-mono text-zinc-50">${stats.price_stats.avg}</p>
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500">Med</p>
                            <p className="text-sm font-mono text-orange-400">${stats.price_stats.median}</p>
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500">Max</p>
                            <p className="text-sm font-mono text-green-400">${stats.price_stats.max}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Hours Stats */}
                    {stats.hours_stats && (
                      <div className="p-3 bg-zinc-800/50 rounded-lg">
                        <p className="text-xs text-zinc-500 mb-2">Орієнтовні години</p>
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div>
                            <p className="text-xs text-zinc-500">Min</p>
                            <p className="text-sm font-mono text-red-400">{stats.hours_stats.min}h</p>
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500">Avg</p>
                            <p className="text-sm font-mono text-zinc-50">{stats.hours_stats.avg}h</p>
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500">Med</p>
                            <p className="text-sm font-mono text-blue-400">{stats.hours_stats.median}h</p>
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500">Max</p>
                            <p className="text-sm font-mono text-green-400">{stats.hours_stats.max}h</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Hourly Rate Stats (calculated) */}
                    {stats.hourly_rate_stats && (
                      <div className="p-3 bg-blue-900/20 rounded-lg border border-blue-800/30">
                        <p className="text-xs text-blue-400 mb-2">Розрахункова ставка (ціна / години)</p>
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div>
                            <p className="text-xs text-zinc-500">Min</p>
                            <p className="text-sm font-mono text-red-400">${stats.hourly_rate_stats.min}/h</p>
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500">Avg</p>
                            <p className="text-sm font-mono text-zinc-50">${stats.hourly_rate_stats.avg}/h</p>
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500">Med</p>
                            <p className="text-sm font-mono text-blue-400">${stats.hourly_rate_stats.median}/h</p>
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500">Max</p>
                            <p className="text-sm font-mono text-green-400">${stats.hourly_rate_stats.max}/h</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Artists list */}
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {stats.artists?.map((a, i) => (
                        <div key={i} className="flex justify-between text-sm px-2 py-1 hover:bg-zinc-800/30 rounded">
                          <span className="text-zinc-400 truncate">{a.name}</span>
                          <div className="flex gap-2 text-xs">
                            {a.price && <span className="text-orange-400">${a.price}</span>}
                            {a.hours && <span className="text-zinc-500">{a.hours}h</span>}
                            {a.hourly_rate && <span className="text-blue-400">${a.hourly_rate}/h</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {Object.keys(data?.declared?.by_service || {}).length === 0 && (
                <Card className="bg-zinc-900/50 border-zinc-800 col-span-full">
                  <CardContent className="py-12 text-center text-zinc-500">
                    <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
                    <p>No declared rates</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* ============ ACTUAL RATES TAB ============ */}
        <TabsContent value="actual" className="space-y-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-green-400 text-lg">Actual Rates</CardTitle>
              <CardDescription className="text-zinc-500">
                Розраховані з виконаних завдань: сума / години = погодинна ставка
              </CardDescription>
            </CardHeader>
          </Card>

          {/* View Mode Toggle */}
          <div className="flex gap-2">
            <Button
              variant={viewMode === "artist" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("artist")}
              className={viewMode === "artist" ? "bg-green-600" : "border-zinc-700"}
            >
              <Users className="h-4 w-4 mr-2" />
              By Artist
            </Button>
            <Button
              variant={viewMode === "service" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("service")}
              className={viewMode === "service" ? "bg-green-600" : "border-zinc-700"}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              By Service Type
            </Button>
          </div>

          {viewMode === "artist" ? (
            <>
              {/* Filters */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                      <Search className="h-4 w-4 text-zinc-500" />
                      <Input
                        placeholder="Search artist..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-zinc-50"
                      />
                    </div>
                    <Select value={filterService} onValueChange={setFilterService}>
                      <SelectTrigger className="w-[180px] bg-zinc-800 border-zinc-700">
                        <SelectValue placeholder="Service Type" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="all" className="text-zinc-50">All Services</SelectItem>
                        {allServiceTypes.map(type => (
                          <SelectItem key={type} value={type} className="text-zinc-50 capitalize">
                            {type.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="w-[150px] bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="name" className="text-zinc-50">Ім'я</SelectItem>
                        <SelectItem value="rating" className="text-zinc-50">Rating</SelectItem>
                        <SelectItem value="rate" className="text-zinc-50">Ставка $/h</SelectItem>
                        <SelectItem value="amount" className="text-zinc-50">Виплачено</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                    >
                      {sortOrder === "asc" ? "↑" : "↓"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Actual Rates Table - By Artist */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800">
                        <TableHead className="text-zinc-400">Artist</TableHead>
                        <TableHead className="text-zinc-400 text-center">Rating</TableHead>
                        <TableHead className="text-zinc-400 text-center">Задач</TableHead>
                        <TableHead className="text-zinc-400 text-right">Годин</TableHead>
                        <TableHead className="text-zinc-400 text-right">Виплачено</TableHead>
                        <TableHead className="text-zinc-400 text-right">$/год</TableHead>
                        <TableHead className="text-zinc-400">Послуги</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortArtists(filteredActualArtists, false).map(artist => {
                        const totalTasks = Object.values(artist.services || {}).reduce((s, srv) => s + srv.tasks_count, 0);
                        return (
                          <TableRow key={artist.id} className="border-zinc-800">
                            <TableCell>
                              <div>
                                <span className="text-zinc-50 font-medium">{artist.name}</span>
                                {artist.position && (
                                  <p className="text-xs text-zinc-500 capitalize">{artist.position.replace(/_/g, ' ')}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              {artist.rating ? (
                                <div className="flex items-center justify-center gap-1">
                                  <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                                  <span className="text-yellow-500 text-sm">{artist.rating}</span>
                                </div>
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-center text-zinc-50">{totalTasks}</TableCell>
                            <TableCell className="text-right text-zinc-400">{artist.total_hours}h</TableCell>
                            <TableCell className="text-right">
                              <span className="text-orange-400 font-mono">${artist.total_amount?.toLocaleString()}</span>
                            </TableCell>
                            <TableCell className="text-right">
                              {artist.overall_hourly_rate ? (
                                <span className="text-green-400 font-mono">${artist.overall_hourly_rate}/h</span>
                              ) : (
                                <span className="text-zinc-600">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {Object.entries(artist.services || {}).map(([type, stats]) => (
                                  <Badge
                                    key={type}
                                    variant="outline"
                                    className="text-[10px] border-zinc-700 text-zinc-400"
                                    title={`${stats.tasks_count} задач, ${stats.total_hours}h, $${stats.total_amount}`}
                                  >
                                    {type.substring(0, 10)}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {filteredActualArtists.length === 0 && (
                    <div className="text-center py-12 text-zinc-500">
                      <Users className="h-10 w-10 mx-auto mb-3 opacity-50" />
                      <p>No completed task data</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            /* Actual Rates - By Service Type */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(data?.actual?.by_service || {}).map(([serviceType, stats]) => (
                <Card key={serviceType} className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-zinc-50 text-lg capitalize flex items-center justify-between">
                      {serviceType.replace(/_/g, ' ')}
                      <Badge className="bg-green-900/30 text-green-400 border-green-700">
                        {stats.tasks_count} задач
                      </Badge>
                    </CardTitle>
                    <CardDescription className="text-zinc-500">
                      {stats.total_hours}h • ${stats.total_amount?.toLocaleString()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Amount Stats */}
                    <div className="p-3 bg-zinc-800/50 rounded-lg">
                      <p className="text-xs text-zinc-500 mb-2">Вартість задачі ($)</p>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-xs text-zinc-500">Min</p>
                          <p className="text-sm font-mono text-red-400">${stats.amount_stats?.min}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500">Avg</p>
                          <p className="text-sm font-mono text-zinc-50">${stats.amount_stats?.avg}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500">Max</p>
                          <p className="text-sm font-mono text-green-400">${stats.amount_stats?.max}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Hourly Rate Stats */}
                    {stats.hourly_rate_stats && (
                      <div className="p-3 bg-zinc-800/50 rounded-lg">
                        <p className="text-xs text-zinc-500 mb-2">Погодинна ставка ($/год)</p>
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div>
                            <p className="text-xs text-zinc-500">Min</p>
                            <p className="text-sm font-mono text-red-400">${stats.hourly_rate_stats.min}</p>
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500">Avg</p>
                            <p className="text-sm font-mono text-zinc-50">${stats.hourly_rate_stats.avg}</p>
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500">Med</p>
                            <p className="text-sm font-mono text-blue-400">${stats.hourly_rate_stats.median}</p>
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500">Max</p>
                            <p className="text-sm font-mono text-green-400">${stats.hourly_rate_stats.max}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {Object.keys(data?.actual?.by_service || {}).length === 0 && (
                <Card className="bg-zinc-900/50 border-zinc-800 col-span-full">
                  <CardContent className="py-12 text-center text-zinc-500">
                    <TrendingUp className="h-10 w-10 mx-auto mb-3 opacity-50" />
                    <p>No completed task data</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ServiceRatesPage;
